import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as platformBillingApi from "@/api/platform-billing.api";
import { setClinicTimezone } from "@/lib/clinic-time";
import { PRP_ENABLED } from "@/lib/hidden-features";
import type {
  PlatformInvoiceListResponse,
  PlatformLedgerInvoice,
  PlatformMeteredUsage,
  PlatformUpcomingInvoice,
  PlatformPlan,
  PlatformSetupIntentResponse,
  PlatformSubscription,
  PlatformUsage,
} from "@/types";

export function usePlatformPlans() {
  return useQuery<PlatformPlan[]>({
    queryKey: ["platformPlans"],
    queryFn: () => platformBillingApi.listPlatformPlans(),
  });
}

/**
 * Whether the clinic is on the PRP Programs plan. Until it loads (or if it
 * fails) the app behaves as a full EHR; the server enforces the plan anyway.
 */
export function usePlanWorkspace() {
  const query = useQuery({
    queryKey: ["planWorkspace"],
    queryFn: () => platformBillingApi.getPlanWorkspace(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  // Scheduling works in clinic time wherever the computer is (lib/clinic-time).
  setClinicTimezone(query.data?.timezone);
  return {
    timezone: query.data?.timezone,
    prpOnly: PRP_ENABLED && query.data?.prp_only === true,
    /** The clinic runs a PRP (PRP Programs plan, or PRP add-on on a full plan). */
    behavioralHealth: PRP_ENABLED && query.data?.behavioral_health === true,
    isLoading: query.isLoading,
  };
}

export function usePlatformSubscription() {
  return useQuery<PlatformSubscription | null>({
    queryKey: ["platformSubscription"],
    queryFn: () => platformBillingApi.getPlatformSubscription(),
  });
}

export function usePlatformUsage() {
  return useQuery<PlatformUsage | null>({
    queryKey: ["platformUsage"],
    queryFn: () => platformBillingApi.getPlatformUsage(),
  });
}

export function usePlatformMeteredUsage() {
  return useQuery<PlatformMeteredUsage | null>({
    queryKey: ["platformMeteredUsage"],
    queryFn: () => platformBillingApi.getPlatformMeteredUsage(),
  });
}

export function usePlatformUpcomingInvoice() {
  return useQuery<PlatformUpcomingInvoice>({
    queryKey: ["platformUpcomingInvoice"],
    queryFn: () => platformBillingApi.getPlatformUpcomingInvoice(),
  });
}

export function usePlatformLedgerInvoice(id: string | null) {
  return useQuery<PlatformLedgerInvoice>({
    queryKey: ["platformLedgerInvoice", id],
    queryFn: () => platformBillingApi.getPlatformLedgerInvoice(id!),
    enabled: Boolean(id),
  });
}

export function usePlatformInvoices(params?: {
  page?: number;
  page_size?: number;
}) {
  return useQuery<PlatformInvoiceListResponse>({
    queryKey: ["platformInvoices", params],
    queryFn: () => platformBillingApi.listPlatformInvoices(params),
  });
}

export function useCreatePlatformSetupIntent() {
  return useMutation<
    PlatformSetupIntentResponse,
    Error,
    { billing_email?: string }
  >({
    mutationFn: (body) => platformBillingApi.createPlatformSetupIntent(body),
  });
}

export function useCreatePlatformSubscription() {
  const queryClient = useQueryClient();
  return useMutation<
    PlatformSubscription,
    Error,
    {
      plan_id: string;
      payment_method_id?: string;
      billing_interval: "month" | "year";
      addon_keys?: string[];
      billing_email?: string;
    }
  >({
    mutationFn: (req) => platformBillingApi.createPlatformSubscription(req),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["platformSubscription"] }),
        queryClient.invalidateQueries({ queryKey: ["platformUsage"] }),
        queryClient.invalidateQueries({ queryKey: ["platformInvoices"] }),
      ]);
    },
  });
}

export function useChangePlatformPlan() {
  const queryClient = useQueryClient();
  return useMutation<
    PlatformSubscription,
    Error,
    {
      plan_id: string;
      billing_interval: "month" | "year";
      proration_behavior?: "create_prorations" | "none" | "always_invoice";
    }
  >({
    mutationFn: (req) => platformBillingApi.changePlatformPlan(req),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["platformSubscription"] }),
        queryClient.invalidateQueries({ queryKey: ["platformUsage"] }),
      ]);
    },
  });
}

export function useCancelPlatformSubscription() {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error>({
    mutationFn: () => platformBillingApi.cancelPlatformSubscription(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["platformSubscription"] });
    },
  });
}

export function usePlatformCustomerPortal() {
  return useMutation<{ url: string }, Error, { returnUrl?: string }>({
    mutationFn: ({ returnUrl }) =>
      platformBillingApi.createPlatformCustomerPortal(returnUrl),
  });
}

export function useUpdatePlatformAddons() {
  const queryClient = useQueryClient();
  return useMutation<PlatformSubscription, Error, { addon_keys: string[] }>({
    mutationFn: (req) => platformBillingApi.updatePlatformAddons(req.addon_keys),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["platformSubscription"] });
    },
  });
}
